import { Outlet, useOutletContext } from "react-router";

/**
 * Renders an `<Outlet />` that re-publishes its parent route's outlet context
 * to descendant routes. Use as the `element` of a pathless wrapper Route
 * inserted only to attach a `loader` (or other Route-level slot) — for
 * example, a layout Route whose loader fetches preferences before the gated
 * child mounts.
 *
 * React Router's bare `<Outlet />` publishes `undefined` as its own context
 * when no `context` prop is passed, so descendants of a pathless wrapper
 * would otherwise read `undefined` from `useOutletContext`. Mounting this
 * component as the wrapper's `element` keeps the grandparent's context
 * visible to the gated child.
 *
 * The `TContext` parameter is informational: TSX call sites do not specify a
 * type argument, so it defaults to `unknown` in practice. Descendants
 * annotate their own `useOutletContext<TContext>()` to recover the concrete
 * shape.
 */
export function InheritOutletContext<TContext = unknown>() {
  const context = useOutletContext<TContext>();
  return <Outlet context={context} />;
}
